import { useCallback, useRef, useEffect } from 'react';

interface Props {
  /** Current split ratio (0-1, left pane fraction) */
  ratio: number;
  /** Called with new ratio while dragging */
  onRatioChange: (ratio: number) => void;
  /** Min left pane fraction (default 0.25) */
  min?: number;
  /** Max left pane fraction (default 0.75) */
  max?: number;
}

export default function ResizeDivider({ ratio, onRatioChange, min = 0.25, max = 0.75 }: Props) {
  const dragging = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    dragging.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return;
    const parent = (e.target as HTMLElement).closest('.main') as HTMLElement | null;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    let newRatio = (e.clientX - rect.left) / rect.width;
    newRatio = Math.max(min, Math.min(max, newRatio));
    onRatioChange(newRatio);
  }, [onRatioChange, min, max]);

  const handlePointerUp = useCallback(() => {
    dragging.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="resize-divider"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={Math.round(ratio * 100)}
      aria-valuemin={Math.round(min * 100)}
      aria-valuemax={Math.round(max * 100)}
      aria-label="Resize panels"
      tabIndex={0}
    >
      <div className="resize-divider-handle" />
    </div>
  );
}
